import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Heart, Rocket, Users, Mail, ArrowRight,
  Sparkles, Coffee, Clock, MapPin, Leaf, Star
} from 'lucide-react';
import PublicNavbar from '../components/PublicNavbar';

const Careers = ({ isInternal = false }) => {
  return (
    <div className={`dark min-h-screen bg-slate-950 text-slate-100 ${isInternal ? 'pt-6' : ''}`} data-testid="careers-page">
      {/* v34.99 (Feb 14 2026) — Switched from custom 4-link navbar (About/Blog/
          Careers/Contact w/ text-slate-600 → unreadable) to <PublicNavbar /> so
          /careers matches /guides + /blog. Old navbar was the cause of the
          "Back to home" pill overlapping the logo. */}
      {!isInternal && <PublicNavbar />}

      {/* Hero Section */}
      <section className={`${isInternal ? 'pt-6 pb-12' : 'pt-32 pb-12'} px-4 sm:px-6 lg:px-8 relative`}>
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent"></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <Badge className="mb-6 bg-amber-500/10 text-amber-400 border border-amber-500/20 px-4 py-2">
            <Leaf className="w-4 h-4 mr-2" />
            Growing Together
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Every Big Dream Starts<br />
            <span className="text-emerald-400">
              with a Small Seed
            </span>
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            We're planting seeds today to grow something beautiful tomorrow. Want to grow with us?
          </p>
        </div>
      </section>

      {/* Main Message */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/10">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-2xl bg-amber-500 bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white text-lg">An Honest Note</p>
                <p className="text-amber-400">From our founder</p>
              </div>
            </div>
            
            <div className="space-y-6 text-slate-300 leading-relaxed text-lg">
              <p>
                Right now, <span className="text-white font-medium">CARTIE<span className="text-emerald-300">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup> is like a 
                <span className="text-amber-400"> young sapling</span> — small, growing, 
                but full of potential. We're not ready to hire yet, but we're getting there.
              </p>
              
              <p>
                I believe in building things the right way. That means 
                <span className="text-white font-medium"> nurturing the foundation first</span>, 
                making sure we have something solid before inviting others to build with us.
              </p>

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6 my-8">
                <p className="text-xl text-amber-300 italic text-center">
                  "When the time comes to hire, I don't want to just fill positions.<br />
                  I want to find people who share our dream."
                </p>
              </div>
              
              <p>
                When we do start hiring, we'll be looking for <span className="text-white font-medium">dreamers and doers</span> — 
                people who believe that even a small tool can make a difference. People who care more about 
                the impact than the title.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Promise */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-emerald-500/20">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <Star className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white text-lg">Our Promise</p>
                <p className="text-emerald-300">What working here will feel like</p>
              </div>
            </div>
            
            <div className="space-y-4 text-slate-300">
              <p className="text-lg">
                When the time is right, here's what you can expect:
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <div className="bg-white/5 rounded-2xl p-5 border border-white/10 hover:border-amber-500/30 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/30 to-orange-500/15 border border-amber-400/40 flex items-center justify-center mb-3 shadow-md shadow-amber-500/20">
                    <Coffee className="w-5 h-5 text-amber-300" />
                  </div>
                  <p className="text-white font-medium">Real Talk, No BS</p>
                  <p className="text-slate-400 text-sm mt-1">No corporate jargon. Just honest conversations.</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-5 border border-white/10 hover:border-emerald-500/30 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/15 border border-emerald-400/40 flex items-center justify-center mb-3 shadow-md shadow-emerald-500/20">
                    <Clock className="w-5 h-5 text-emerald-300" />
                  </div>
                  <p className="text-white font-medium">Flexibility First</p>
                  <p className="text-slate-400 text-sm mt-1">Work when you're at your best. We trust you.</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-5 border border-white/10 hover:border-emerald-500/30 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/15 border border-emerald-400/40 flex items-center justify-center mb-3 shadow-md shadow-emerald-500/20">
                    <MapPin className="w-5 h-5 text-emerald-300" />
                  </div>
                  <p className="text-white font-medium">Work from Anywhere</p>
                  <p className="text-slate-400 text-sm mt-1">Great talent isn't limited by geography.</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-5 border border-white/10 hover:border-emerald-500/30 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/15 border border-emerald-400/40 flex items-center justify-center mb-3 shadow-md shadow-emerald-500/20">
                    <Sparkles className="w-5 h-5 text-emerald-300" />
                  </div>
                  <p className="text-white font-medium">Your Work Matters</p>
                  <p className="text-slate-400 text-sm mt-1">Small team = real impact. No being a cog.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Roles */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-emerald-500/20 shadow-[0_0_60px_-20px_rgba(16,185,129,0.4)]">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white text-lg">Future Teammates</p>
                <p className="text-emerald-300">Roles we'll need someday</p>
              </div>
            </div>
            
            <div className="space-y-4 text-slate-300">
              <p>
                When we grow, these are the kind of people we'll be looking for:
              </p>

              <div className="space-y-3 mt-6">
                {[
                  { role: 'Full-Stack Engineers', desc: 'React + Python lovers who ship clean code' },
                  { role: 'Senior FinOps Engineers', desc: 'Cloud cost detectives who find the wins' },
                  { role: 'AI Engineers', desc: 'Build prediction models that pay for themselves' },
                  { role: 'Cloud Infrastructure Specialists', desc: 'AWS / Azure / GCP / Snowflake / Databricks / DigitalOcean experts who think in costs' },
                  { role: 'ML/Data Engineers', desc: 'Turn data into insights that save money' },
                  { role: 'Developer Advocates', desc: 'Half engineer, half storyteller' },
                  { role: 'Customer Success', desc: 'Help our users get the most out of CARTIEAI' },
                  { role: 'Product Designer', desc: 'Make complex things feel simple' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10 hover:border-emerald-500/30 transition-colors"
                    data-testid={`future-teammate-${idx}`}
                  >
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.6)] shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">{item.role}</p>
                      <p className="text-slate-400 text-sm mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-sm text-slate-400 mt-6 italic">
                Not on this list but think you'd fit? Tell us anyway. The best hires usually come from people who pitch themselves into roles we hadn't named yet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stay Connected */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-rose-500/10 to-pink-500/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-rose-500/20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-rose-500 bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-rose-500/20">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">
              Want to Be First in Line?
            </h2>
            <p className="text-slate-300 mb-8 max-w-md mx-auto">
              Drop us a note. When we start hiring, we'll reach out to the dreamers who believed in us early.
            </p>
            <Link to={isInternal ? "/app/contact" : "/contact"}>
              <Button className="bg-rose-500 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-400 hover:to-pink-400 text-white px-8 shadow-lg shadow-rose-500/20">
                Say Hello
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <p className="text-sm text-slate-400 mt-6">
              No resume needed. Just tell us who you are and why you care.
            </p>
          </div>
        </div>
      </section>

      {/* Footer - Only show for public page */}
      {!isInternal && (
      <footer className="py-8 bg-slate-950 text-slate-600 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-6 h-6" />
            <span className="text-sm">
              <span className="text-white">CARTIE<span className="text-emerald-300">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
            </span>
          </div>
          <p className="text-sm" data-testid="careers-footer-entity">© 2026 CARTIEAI LLC · Texas, USA · Growing one day at a time.</p>
        </div>
      </footer>
      )}
    </div>
  );
};

export default Careers;
